/**
 * Light, dark, or whatever the operating system says.
 *
 * The choice lives in `localStorage['glee.theme']`. 'light' and 'dark' are the
 * values the old two-way toggle wrote, so somebody who picked one before keeps
 * it; anything else -- including nothing at all -- means 'system'.
 *
 * Applied before React mounts (`initTheme` in main.tsx) so the login page and
 * the first paint of the shell are already in the right theme. Previously only
 * the authenticated shell set the class, so a dark-mode user saw a white login
 * page flash before every session.
 */

import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

const KEY = 'glee.theme';
const QUERY = '(prefers-color-scheme: dark)';

export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Storage blocked (private window, locked-down browser): follow the OS.
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.(QUERY).matches;
}

export function resolveTheme(pref: ThemePreference): 'light' | 'dark' {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

export function applyTheme(pref: ThemePreference): void {
  const resolved = resolveTheme(pref);
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  // Native controls (scrollbars, date pickers, <select> popups) follow too.
  root.style.colorScheme = resolved;
}

/** Call once, before the first render. */
export function initTheme(): void {
  applyTheme(readThemePreference());
  // Follow the OS live while the preference is 'system', on every page --
  // including the ones outside the shell.
  window.matchMedia?.(QUERY).addEventListener?.('change', () => {
    if (readThemePreference() === 'system') applyTheme('system');
  });
}

/** The current preference, and a setter that persists and applies it. */
export function useTheme(): {
  preference: ThemePreference;
  resolved: 'light' | 'dark';
  setPreference: (next: ThemePreference) => void;
} {
  const [preference, setPref] = useState<ThemePreference>(readThemePreference);
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(preference));

  useEffect(() => {
    applyTheme(preference);
    setResolved(resolveTheme(preference));
    if (preference !== 'system') return;
    const media = window.matchMedia?.(QUERY);
    const onChange = () => setResolved(resolveTheme('system'));
    media?.addEventListener?.('change', onChange);
    return () => media?.removeEventListener?.('change', onChange);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // Not persisted, but still applied for this session.
    }
    setPref(next);
  }, []);

  return { preference, resolved, setPreference };
}
